import React, { useEffect, useId, useState } from "react";
import styled, { useTheme } from "styled-components";
import ConversationsList from "./ConversationsList";
import { useTranslate } from '@tolgee/react';
import ConversationView from "./ConversationView";

import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import ChatDetailsView from "./ChatDetailsView";
import GroupDetailsView from "../GroupDetails/GroupDetailsView";
import ChatDetailsHeader from "./ChatDetailsHeader";
import { useConversationService } from "../../Context/ConversationsContext";
import AddNewChatModal from "./AddNewChatModal";
import IconButton from "../GenericComponents/IconButton";
import { ReactComponent as NewChat } from "../../Assets/iconsv2/generic/newChat.svg";
import useDraftMessages from "../../hooks/useDraftMessage";
import CallChatDetailsView from '../CallChatComponents/CallChatDetailsView';

import { autoUpdate, flip, offset, useClick, useDismiss, useFloating, useInteractions, useRole } from "@floating-ui/react";

const ChatWrapper = ({
  conversations,
  selectedConversation,
  handleSelectConversation,
  loadConversations,
  handleOpenSubscriptionModal,
  shouldScrollBottom,
  inputValue,
  handleInputChange,
  handleSendButton,
  messages,
  handleReactToMessage,
  handleRemoveOwnMessageReaction,
  handleShowFullScreenImg,
  handleDeleteMessage,
  displayRightClickMenu,
  mediaInputFileRef,
  fileInputRef,
  scrollHeightRef,
  setMediaInput,
  setFileInput,
  mediaInput,
  showEditMessageModal,
  selectedMessageForContextMenu,
  handleCloseEdit,
  messageInputRef,
  showReplyMessageModal,
  handleCloseReply,
  fileInput,
  shouldLoadMoreMessages,
  handleGetMoreMessages,
  showBlockedChatModal,
  setShowBlockedChatModal,
  handleUnblockContact,
  messageMood,
  setMessageMood,
  handleShowConversationDetailsMenu,
  handleCreateCallHeaderClick,
  contacts,
  handleInviteContact,
  loadContacts,
  handleBlockContact,
  handleShowGroupChatDetails,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const {
    showGroupChatDetails,
    setLoading,
    isAddNewChatOpen,
    setIsAddNewChatOpen,
  } = useGenericComponentsStateService();

  const { 
    getDraftMessage, 
    saveDraftMessage, 
    clearDraftMessage, 
    hasDraftMessage,
    getReplyState,
    hasReplyState,
  } = useDraftMessages();

  const { setConversations, setSelectedConversation } =
    useConversationService();
  const theme = useTheme();

  const { refs, floatingStyles, context } = useFloating({
    open: isAddNewChatOpen,
    onOpenChange: setIsAddNewChatOpen,
    placement: "right-end",
    middleware: [offset(10), flip()], // Avoids clipping and sets offset
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  // useEffect(() => {
  //   loadConversations()
  // }, [])
  const { t } = useTranslate();
  return (
    <Container>
      <Main>
        <Header>
          <HeaderTitle>{t('general.chats')}</HeaderTitle>
          <div ref={refs.setReference}    {...getReferenceProps()}>
            <IconButton
              icon={<NewChat />}
              onClick={() => {
                setIsAddNewChatOpen(true);
              }}
            />
          </div>
        </Header>
        <ConversationsList
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          conversations={conversations}
          selectedConversation={selectedConversation}
          handleSelectConversation={handleSelectConversation}
          loadConversations={loadConversations}
          handleOpenSubscriptionModal={handleOpenSubscriptionModal}
          getDraftMessage={getDraftMessage}
          hasDraftMessage={hasDraftMessage}
          getReplyState={getReplyState}
          hasReplyState={hasReplyState}
        />
      </Main>

      <Details>
        {selectedConversation ? (
          <ConversationView
            key={"View"}
            shouldScrollBottom={shouldScrollBottom}
            inputValue={inputValue}
            handleInputChange={handleInputChange}
            handleSendButton={handleSendButton}
            messages={messages}
            selectedConversation={selectedConversation}
            handleReactToMessage={handleReactToMessage}
            handleRemoveOwnMessageReaction={handleRemoveOwnMessageReaction}
            handleShowFullScreenImg={handleShowFullScreenImg}
            handleDeleteMessage={handleDeleteMessage}
            displayRightClickMenu={displayRightClickMenu}
            mediaInputFileRef={mediaInputFileRef}
            fileInputRef={fileInputRef}
            scrollHeightRef={scrollHeightRef}
            setMediaInput={setMediaInput}
            setFileInput={setFileInput}
            mediaInput={mediaInput}
            showEditMessageModal={showEditMessageModal}
            selectedMessageForContextMenu={selectedMessageForContextMenu}
            handleCloseEdit={handleCloseEdit}
            messageInputRef={messageInputRef}
            showReplyMessageModal={showReplyMessageModal}
            handleCloseReply={handleCloseReply}
            fileInput={fileInput}
            shouldLoadMoreMessages={shouldLoadMoreMessages}
            handleGetMoreMessages={handleGetMoreMessages}
            showBlockedChatModal={showBlockedChatModal}
            setShowBlockedChatModal={setShowBlockedChatModal}
            handleUnblockContact={handleUnblockContact}
            messageMood={messageMood}
            setMessageMood={setMessageMood}
            handleShowConversationDetailsMenu={
              handleShowConversationDetailsMenu
            }
            handleCreateCallHeaderClick={handleCreateCallHeaderClick}
            saveDraftMessage={saveDraftMessage}
            clearDraftMessage={clearDraftMessage}
          />
        ) : (
          !isAddNewChatOpen && (
            <StartChatWrapper>
              <IconButton
                onClick={() => setIsAddNewChatOpen(true)}
                style={{
                  background: theme.chat.selectedBackground,
                  padding: "10px",
                  borderRadius: "4px",
                }}
                icon={<NewChat color={theme.menu.selectedTextColor} />}
              />
              <p>{t('new_chat.start_new_chat')}</p>
            </StartChatWrapper>
          )
        )}

        {isAddNewChatOpen && (
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              display: "flex",
              flexDirection: "column",
              width: "400px",
              zIndex: "1",
              maxHeight: "90vh", // 👈 here is the important part
            }}
          >
            <AddNewChatModal
              contacts={contacts}
              handleSelectConversation={handleSelectConversation}
              handleInviteContact={handleInviteContact}
              loadConversations={loadConversations}
              setSelectedConversation={setSelectedConversation}
            />
          </div>
        )}

        {showGroupChatDetails && !selectedConversation?.with_emotii && (
          <ChatDetailsWrapper>
            <ChatDetailsHeader
              selectedConversation={selectedConversation}
              handleClose={() => handleShowGroupChatDetails()}
            />
            {selectedConversation?.group && (
              <GroupDetailsView
                groupData={selectedConversation}
                setConversations={setConversations}
                setSelectedConversation={setSelectedConversation}
                contacts={contacts}
                setLoading={setLoading}
                loadContacts={loadContacts}
                handleInviteContact={handleInviteContact}
              />
            )}

            {selectedConversation?.is_call_chat && !selectedConversation?.group && (
              <CallChatDetailsView
                conversationData={selectedConversation}
                setConversations={setConversations}
                setSelectedConversation={setSelectedConversation}
                contacts={contacts}
                setLoading={setLoading}
                loadContacts={loadContacts}
                handleInviteContact={handleInviteContact}
              />
            )}

            {!selectedConversation?.group && 
              !selectedConversation?.is_call_chat && 
              selectedConversation?.other_participants?.length > 0 && (
              <ChatDetailsView
                conversationData={selectedConversation.other_participants[0]}
                handleBlockContact={handleBlockContact}
                handleUnblockContact={handleUnblockContact}
              />
            )}
          </ChatDetailsWrapper>
        )}
      </Details>
    </Container>
  );
};

export default ChatWrapper;

const Container = styled.div`
  display: flex;
  width: 100%;
  overflow:hidden;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 419px;
  min-width:400px;
  width: 100%;
  background: ${({ theme }) => theme.menu.background};
  border-right: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 16px;
  align-items: center;
  background: ${({ theme }) => theme.menu.background};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  gap: 20px;
`;
const HeaderTitle = styled.h3`
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;
const Details = styled.div`
  width: 100%;
  display: flex;
    overflow:hidden;
  background: ${({ theme }) => theme.menu.background};
`;

const ChatDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 350px;
  min-width:350px;
  position: relative;
  border-left: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.background};

`;

const StartChatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin: auto;
  color: ${({ theme }) => theme.menu.selectedTextColor};
`;
